import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConvertDateToString } from '../../shared/utils/convert-date-to-string.util';

export interface ActiveFilter {
  label: string;
  keys: string[];
}

const NUMBER_RANGE_FILTER_GROUPS: { label: string; keys: string[] }[] = [
  {
    label: 'Paid Amount',
    keys: ['filter_by_amount_min', 'filter_by_amount_max'],
  },
  {
    label: 'Authorized Amount',
    keys: [
      'filter_by_authorized_amount_min',
      'filter_by_authorized_amount_max',
    ],
  },
];
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  public allParams: any = {};
  private paginationParams?: any;
  public activeFilters: ActiveFilter[] = [];

  constructor(private router: Router, private route: ActivatedRoute) {}

  setPagination(
    page: number,
    length: number,
    basicParams: Record<string, any> = {}
  ): void {
    this.paginationParams = {
      page,
      length,
    };
    this.allParams = { ...this.allParams, ...basicParams };
    this.navigate();
  }

  setParamsToRoute(
    searchBy?: string,
    searchText?: string,
    sortBy?: string,
    filterFormValue?: Record<string, string | number[] | Date | null>,
    basicParams: Record<string, any> = {},
    shouldSendCategoriesInSeparateArray: boolean = false,
    resetPage: boolean = true
  ): void {
    let params;

    if (!shouldSendCategoriesInSeparateArray) {
      params = {
        [searchBy ?? 'global_search']: searchText ?? '',
        sort: sortBy ?? '',
        ...filterFormValue,
      };
    } else {
      params = {
        search_category: searchBy,
        search_value: searchText,
        sort: sortBy ?? '',
        ...filterFormValue,
      };
    }

    const paramsFromRoute = this.route.snapshot.queryParams;
    const currentLength =
      this.paginationParams?.length ??
      (paramsFromRoute['length'] ? Number(paramsFromRoute['length']) : undefined);

    if (resetPage) {
      this.paginationParams = currentLength
        ? { page: 1, length: currentLength }
        : { page: 1 };
    } else if (paramsFromRoute['page']) {
      this.paginationParams = {
        page: Number(paramsFromRoute['page']),
        ...(currentLength ? { length: currentLength } : {}),
      };
    }
    if (resetPage && this.paginationParams) {
      this.paginationParams.page = 1;
    }
    // Remove sort and search keys from basicParams so new values take precedence
    const { sort, ...basicParamsWithoutSort } = basicParams;
    // Remove all search-related keys from basicParams to prevent old values from overwriting new ones
    Object.keys(basicParamsWithoutSort).forEach((key) => {
      if (key.startsWith('search_by_') || key === 'global_search' || key === 'search_category' || key === 'search_value') {
        delete basicParamsWithoutSort[key];
      }
    });
    this.allParams = { ...params, ...basicParamsWithoutSort };
    this.setActiveFilters();
    this.navigate();
  }

  private navigate(): void {
    const sanitizedParams = {};
    Object.keys(this.allParams).forEach((key: string) => {
      if (typeof this.allParams[key] === 'string') {
        if (this.allParams[key]?.length) {
          Object.assign(sanitizedParams, { [key]: this.allParams[key] });
        }
      }
      if (
        typeof this.allParams[key] === 'number' &&
        Number.isFinite(this.allParams[key])
      ) {
        Object.assign(sanitizedParams, { [key]: this.allParams[key] });
      }
      if (Array.isArray(this.allParams[key]) && this.allParams[key]?.length) {
        let valueToSet = this.allParams[key]?.join(',');
        if (key.includes('date')) {
          const startDate = this.allParams[key][0]
            ? ConvertDateToString(this.allParams[key][0], '-')
            : '';
          const endDate = this.allParams[key][1]
            ? ConvertDateToString(this.allParams[key][1], '-')
            : '';
          if (startDate && endDate) {
            valueToSet = startDate + ',' + endDate;
          } else {
            valueToSet = startDate || endDate;
          }
        }
        if (valueToSet) {
          Object.assign(sanitizedParams, { [key]: valueToSet });
        }
      }
      if (this.allParams[key] instanceof Date) {
        Object.assign(sanitizedParams, {
          [key]: ConvertDateToString(this.allParams[key], '-'),
        });
      }
      if (key === 'filter_by_last_status_change_date' && this.allParams[key]) {
        const date = ConvertDateToString(this.allParams[key], '-');
        Object.assign(sanitizedParams, { [key]: date });
      }
    });
    if (this.paginationParams) {
      Object.assign(sanitizedParams, this.paginationParams);
    }

    // console.log(sanitizedParams);
    this.router.navigate([], { queryParams: sanitizedParams });
  }

  setActiveFilters() {
    const activeKeys: string[] = [];
    Object.keys(this.allParams).forEach((key: string) => {
      const value = this.allParams[key];
      if (Array.isArray(value) ? value?.length : value || value === 0) {
        activeKeys.push(key);
      }
    });
    this.activeFilters = [];
    const groupedKeys = new Set<string>();
    NUMBER_RANGE_FILTER_GROUPS.forEach((group) => {
      const presentKeys = group.keys.filter((key) => activeKeys.includes(key));
      if (presentKeys.length) {
        this.activeFilters.push({ label: group.label, keys: presentKeys });
        presentKeys.forEach((key) => groupedKeys.add(key));
      }
    });
    activeKeys.map((key: string) => {
      if (groupedKeys.has(key) || key === 'search_category') {
        return;
      }
      switch (key) {
        case 'search_value': {
          const keys = [];
          if (activeKeys?.find((k) => k === 'search_category')) {
            keys.push('search_category');
          }
          keys.push(key);
          this.activeFilters.push({ label: 'Search', keys });
          break;
        }
        case 'global_search': {
          this.activeFilters.push({ label: 'Search', keys: [key] });
          break;
        }
        case 'sort': {
          this.activeFilters.push({ label: 'Sort', keys: [key] });
          break;
        }
        default: {
          if (key.includes('filter_by')) {
            this.activeFilters.push({
              label: this.transformToTitleCase(key, true),
              keys: [key],
            });
            break;
          }
          this.activeFilters.push({
            label: this.transformToTitleCase(key),
            keys: [key],
          });
        }
      }
    });

    // console.log('activeFilters', this.activeFilters);
  }

  removeActiveFilter(filter: ActiveFilter) {
    filter?.keys.forEach((key: string) => {
      this.allParams[key] = undefined;
    });
    this.paginationParams = this.paginationParams?.length
      ? { page: 1, length: this.paginationParams.length }
      : { page: 1 };
    this.setActiveFilters();
    if (this.paginationParams) {
      this.paginationParams.page = 1;
    }
    this.navigate();
  }

  transformToTitleCase(input: string, removePrefix = false): string {
    const words = input
      .split('_')
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
    return removePrefix ? words.slice(2).join(' ') : words.join(' ');
  }

  clearParams(): void {
    this.activeFilters = [];
    this.allParams = {};
    this.paginationParams = {};
  }
}
